export type ForgeStageKey =
  | 'auto'
  | 'blueprint'
  | 'born_in_fire'
  | 'through_the_heat'
  | 'iron_house'
  | 'stock_removal'
  | 'the_grip'
  | 'marrying_materials'
  | 'final_polish'
  | 'the_edge'
  | 'ready_for_legacy';

// Each stage carries TWO wordings, deliberately kept side by side in this one file
// so nobody updates one and forgets the other:
//   description        — present-tense live status, used by the batch tracker / drops copy
//   galleryDescription — retrospective wording, used by the Process Gallery
export type ForgeStage = {
  key: ForgeStageKey;
  title: string;
  description: string;
  galleryDescription?: string;
};


export const FORGE_STAGES: ForgeStage[] = [
  {
    key: 'auto',
    title: 'The Next Batch Is On The Anvil',
    description:
      "Our knives are forged in small batches. \u00a0\nClaim your spot early—past releases have sold out fast.",
  },
  {
    key: 'blueprint',
    title: 'The Blueprint',
    description:
      'Mapping out the custom steel composition and design geometry for the upcoming batch.',
    galleryDescription:
      'Each batch begins with its steel composition and design geometry mapped out.',
  },
  {
    key: 'born_in_fire',
    title: 'Born in Fire',
    description:
      "We're at the anvil, stacking, welding, forging, and pressing the raw billets to shape.",
    galleryDescription:
      'At the anvil — stacking, welding, forging and pressing the raw billets to shape.',
  },
  {
    key: 'through_the_heat',
    title: 'Through the Heat',
    description:
      "Heat treating and quenching the batch to lock in the steel's ultimate durability.",
    galleryDescription:
      "Heat treating and quenching, locking in the steel's ultimate durability.",
  },
  {
    key: 'iron_house',
    title: 'In the Iron-House',
    description:
      'The bladesmith is actively drawing out, cutting, and shaping the high-carbon steel.',
    galleryDescription: 'Drawing out, cutting and shaping the high-carbon steel.',
  },
  {
    key: 'stock_removal',
    title: 'Stock Removal & Grinding',
    description:
      'The billets are on the 84 Engineering belt grinder to refine character and edge geometry.',
    galleryDescription:
      'On the 84 Engineering belt grinder, refining character and edge geometry.',
  },
  {
    key: 'the_grip',
    title: 'The Grip',
    description:
      'Designing and shaping the unique handles, including all material selection.',
    galleryDescription:
      'Designing and shaping the handles, and selecting every material that goes into them.',
  },
  {
    key: 'marrying_materials',
    title: 'The Marrying of Materials',
    description:
      'Carefully gluing up and pinning the handle pieces firmly into place.',
    galleryDescription: 'Gluing up and pinning the handle pieces firmly into place.',
  },
  {
    key: 'final_polish',
    title: 'The Final Polish',
    description:
      'The blades are undergoing the tedious, painstaking process of hand-sanding and sealing.',
    galleryDescription: 'The tedious, painstaking work of hand-sanding and sealing.',
  },
  {
    key: 'the_edge',
    title: 'The Edge',
    description:
      'Honed to a "market-ready" edge so browsers can handle the steel without losing a finger (mostly). The literal, shaving-sharp razor edge is applied the moment a piece is sold.',
    galleryDescription:
      'Honed to a "market-ready" edge so browsers can handle the steel without losing a finger (mostly). The literal, shaving-sharp razor edge is applied the moment a piece is sold.',
  },
  {
    key: 'ready_for_legacy',
    title: 'Ready for Legacy',
    description:
      'Dropped on the shelf! The batch is fully crafted, polished, and ready for you to claim.',
    galleryDescription: 'Fully crafted, polished, and on the shelf ready to claim.',
  },
];


export function getForgeStage(key: string | null | undefined): ForgeStage {
  if (!key) return FORGE_STAGES[0];
  return FORGE_STAGES.find((s) => s.key === key) ?? FORGE_STAGES[0];
}
